import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Mail, Loader2, CheckCircle } from "lucide-react";

// Task #248 — Shared platform (Super Admin) Gmail connector. Used in BOTH the
// personal Settings page and the Email Settings page. Connect is send-only by
// default; inbox reading follows the gated request → self-approve (in Gmail Read
// Access) → enable flow, mirroring the tenant connector.

type PlatformGmailStatus = {
  connected: boolean;
  email: string | null;
  inboundReadingAvailable: boolean;
  readAccessState: "none" | "requested" | "rejected" | "granted_pending_connect" | "connected";
  readRequestDecisionNote: string | null;
};

export function PlatformGmailConnector({ returnUrl }: { returnUrl?: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery<PlatformGmailStatus>({
    queryKey: ["/api/platform/gmail/status"],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/status"] });

  const currentUrl = () => returnUrl || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/platform/account");

  const connectMutation = useMutation({
    mutationFn: async () => {
      // Use raw fetch (GET needs no CSRF token) so we can inspect the HTTP
      // status and route a lapsed platform session through re-login instead
      // of showing a dead-end toast.
      const res = await fetch(
        `/api/platform/gmail/auth?returnUrl=${encodeURIComponent(currentUrl())}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.message || "Failed to start Gmail connect");
        (err as any).status = res.status;
        throw err;
      }
      return res.json();
    },
    onSuccess: (data: { authUrl: string }) => { window.location.href = data.authUrl; },
    onError: (e: any) => {
      // Platform session expired — route to re-login so the admin can finish
      // connecting Gmail (matches the tenant connector behaviour).
      if (e?.status === 401) {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/api/login?silent=1&returnTo=${returnTo}`;
        return;
      }
      toast({ title: "Error", description: e?.message || "Failed to start Gmail connect", variant: "destructive" });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/gmail/disconnect")).json(),
    onSuccess: () => { invalidate(); toast({ title: "Disconnected", description: "Gmail disconnected." }); },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to disconnect", variant: "destructive" }),
  });

  const requestReadMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/gmail/read/request", { reason: "" })).json(),
    onSuccess: () => { invalidate(); toast({ title: "Requested", description: "Inbox read access requested — approve it in Gmail Read Access." }); },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to request read access", variant: "destructive" }),
  });

  const appealReadMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/gmail/read/appeal", { appealText: "" })).json(),
    onSuccess: () => { invalidate(); toast({ title: "Appeal submitted", description: "Your request is pending review again." }); },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to appeal", variant: "destructive" }),
  });

  const enableReadMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", `/api/platform/gmail/read/auth?returnUrl=${encodeURIComponent(currentUrl())}`);
      return res.json();
    },
    onSuccess: (data: { authUrl: string }) => { window.location.href = data.authUrl; },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to enable inbox reading", variant: "destructive" }),
  });

  return (
    <div className="flex items-start justify-between py-2" data-testid="platform-gmail-connector">
      <div className="flex items-start gap-3">
        <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
        <div>
          <span className="font-medium text-sm">Gmail</span>
          {status?.connected && status?.email && (
            <p className="text-xs text-muted-foreground" data-testid="text-platform-gmail-email">{status.email}</p>
          )}

          {/* Read-access funnel — only relevant once a send connection exists. */}
          {status?.connected && (
            <div className="mt-1.5 space-y-1.5" data-testid="section-platform-gmail-read">
              {status.inboundReadingAvailable ? (
                <p className="text-xs text-green-600" data-testid="text-platform-read-active">✓ Inbox reading active</p>
              ) : status.readAccessState === "granted_pending_connect" ? (
                <div className="space-y-1">
                  <p className="text-xs text-blue-600" data-testid="text-platform-read-granted">
                    Inbox read access approved — finish connecting to enable it.
                  </p>
                  <p className="text-[11px] text-muted-foreground max-w-md">
                    This permanently uses one of a limited number of Google read seats and can't be undone.
                  </p>
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => enableReadMutation.mutate()}
                    disabled={enableReadMutation.isPending}
                    data-testid="button-platform-enable-read"
                  >
                    {enableReadMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Enable inbox reading
                  </Button>
                </div>
              ) : status.readAccessState === "requested" ? (
                <p className="text-xs text-amber-600" data-testid="text-platform-read-requested">
                  Inbox read access requested — pending review.
                </p>
              ) : status.readAccessState === "rejected" ? (
                <div className="space-y-1">
                  <p className="text-xs text-red-600" data-testid="text-platform-read-rejected">
                    Inbox read access was declined{status.readRequestDecisionNote ? `: ${status.readRequestDecisionNote}` : "."}
                  </p>
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => appealReadMutation.mutate()}
                    disabled={appealReadMutation.isPending}
                    data-testid="button-platform-appeal-read"
                  >
                    {appealReadMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Appeal decision
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-[11px] text-muted-foreground max-w-md">
                    Inbox reading (reply detection, unified inbox) needs separate approval. Sending works without it.
                  </p>
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => requestReadMutation.mutate()}
                    disabled={requestReadMutation.isPending}
                    data-testid="button-platform-request-read"
                  >
                    {requestReadMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Request inbox read access
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : status?.connected ? (
          <>
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3.5 h-3.5" /> Connected
            </span>
            <Button
              size="sm" variant="ghost"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              data-testid="button-platform-disconnect-gmail"
            >
              {disconnectMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Disconnect"}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            data-testid="button-platform-connect-gmail"
          >
            {connectMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Connect
          </Button>
        )}
      </div>
    </div>
  );
}
